import React, { useState } from 'react';
import { formatCurrency, formatDate, generateInvoiceNumber, saveInvoice } from '../utils/invoiceUtils';
import { downloadInvoicePDF } from '../utils/pdfGenerator';

const InvoicePreview = ({ invoiceData, onBack, onSave }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    try {
      await downloadInvoicePDF(invoiceData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
    setIsGenerating(false);
  };

  const handleSaveAndGenerate = async () => {
    setIsSaving(true);
    try {
      const finalInvoiceNumber = generateInvoiceNumber(invoiceData.matterCode, 'INV');
      const finalInvoiceData = { ...invoiceData, invoiceNumber: finalInvoiceNumber };
      saveInvoice(finalInvoiceData);
      await downloadInvoicePDF(finalInvoiceData);
      setSaved(true);
      if (onSave) onSave(finalInvoiceData);
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Error saving invoice. Please try again.');
    }
    setIsSaving(false);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Action Bar */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex items-center justify-between sticky top-4 z-10">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-800">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Edit
        </button>
        <div className="flex gap-3">
          <button onClick={handleDownloadPDF} disabled={isGenerating} className="btn-secondary flex items-center">
            {isGenerating ? (
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            {isGenerating ? 'Generating...' : 'Download Preview'}
          </button>
          <button onClick={handleSaveAndGenerate} disabled={isSaving || saved} className="btn-primary flex items-center">
            {isSaving ? (
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : saved ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
            )}
            {isSaving ? 'Saving...' : saved ? 'Saved!' : 'Save & Generate Final'}
          </button>
        </div>
      </div>

      {/* Invoice Preview */}
      <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header Image */}
        <div className="px-[4.6mm] pt-[6mm]">
          <img 
            src="/remington-header.png" 
            alt="Remington LLP Header" 
            className="w-full h-auto"
            style={{ maxHeight: '60px' }}
          />
        </div>

        {/* PIN Number */}
        <div className="px-8 text-right">
          <span className="text-xs text-gray-600">P051958154Y</span>
        </div>

        {/* Invoice Title & Meta */}
        <div className="px-8 py-6 flex justify-between items-end">
          <h2 className="text-5xl font-bold text-red-800 tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
            INVOICE
          </h2>
          <div className="text-right">
            <p className="text-gray-600">Code: R – {invoiceData.matterCode}</p>
            <p className="text-gray-600">Invoice No: <span className="font-bold text-red-800">{invoiceData.invoiceNumber}</span></p>
            <p className="text-gray-600">Date: {formatDate(invoiceData.date)}</p>
          </div>
        </div>

        {/* Billed To */}
        <div className="px-8 py-4 flex justify-between">
          <div>
            <p className="font-semibold text-gray-800 mb-2">Billed to:</p>
            <p className="text-gray-700">{invoiceData.client.name}</p>
            {invoiceData.client.organization && <p className="text-gray-700">{invoiceData.client.organization}</p>}
            {invoiceData.client.address.map((line, idx) => <p key={idx} className="text-gray-700">{line}</p>)}
            {invoiceData.client.phone && <p className="text-gray-700">Tel: {invoiceData.client.phone}</p>}
            {invoiceData.client.email && <p className="text-red-800">Email: {invoiceData.client.email}</p>}
          </div>
          <div className="text-right text-red-800">
            <p>Delivery by {invoiceData.deliveryMethod}</p>
            <p>Advanced copy by email</p>
          </div>
        </div>

        {/* Matter Description */}
        <div className="mx-8 my-4 bg-red-800 text-white px-4 py-3 text-sm font-semibold">
          {invoiceData.invoiceType} Relative to {invoiceData.matterDescription}
        </div>

        {/* Professional Fees */}
        <div className="px-8 py-4">
          <h3 className="text-red-800 font-bold mb-4"><span className="mr-4">I.</span>Professional Fees</h3>
          {invoiceData.professionalFees.map((category, catIdx) => (
            <div key={catIdx} className="mb-4">
              <p className="font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2">▪</span>
                <span className="border-b border-gray-300">{category.category}</span>
              </p>
              {category.items.map((item, itemIdx) => (
                <div key={itemIdx} className="flex justify-between items-start ml-6 mb-2">
                  <div className="flex-1">
                    <span className="text-gray-500 mr-2">{itemIdx + 1}.</span>
                    <span className="text-gray-700">{item.description}</span>
                  </div>
                  <div className="flex items-center ml-4">
                    <span className="text-gray-500 mr-4">KES</span>
                    <span className="text-gray-800 font-mono w-28 text-right">{formatCurrency(item.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="flex justify-between items-center border-t border-gray-300 pt-2 mt-4">
            <span className="font-semibold text-gray-700 ml-6">Subtotal Professional Fees</span>
            <div className="flex items-center">
              <span className="text-gray-500 mr-4">KES</span>
              <span className="text-gray-800 font-bold font-mono w-28 text-right border-b-2 border-gray-400">{formatCurrency(invoiceData.subtotalProfessionalFees)}</span>
            </div>
          </div>
        </div>

        {/* Outlays */}
        <div className="px-8 py-4">
          <h3 className="text-red-800 font-bold mb-4"><span className="mr-4">II.</span>Outlays</h3>
          {invoiceData.outlays.map((category, catIdx) => (
            <div key={catIdx} className="mb-4">
              <p className="font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2">▪</span>
                <span className="border-b border-gray-300">{category.category}</span>
              </p>
              {category.items.map((item, itemIdx) => (
                <div key={itemIdx} className="flex justify-between items-start ml-6 mb-2">
                  <div className="flex-1">
                    <span className="text-gray-500 mr-2">{itemIdx + 1}.</span>
                    <span className="text-gray-700">{item.description}</span>
                  </div>
                  <div className="flex items-center ml-4">
                    <span className="text-gray-500 mr-4">KES</span>
                    <span className="text-gray-800 font-mono w-28 text-right">{formatCurrency(item.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="flex justify-between items-center border-t border-gray-300 pt-2 mt-4">
            <span className="font-semibold text-gray-700 ml-6">Subtotal Outlays</span>
            <div className="flex items-center">
              <span className="text-gray-500 mr-4">KES</span>
              <span className="text-gray-800 font-bold font-mono w-28 text-right border-b-2 border-gray-400">{formatCurrency(invoiceData.subtotalOutlays)}</span>
            </div>
          </div>
        </div>

        {/* Taxes */}
        <div className="px-8 py-4">
          <h3 className="text-red-800 font-bold mb-4"><span className="mr-4">III.</span>Taxes</h3>
          <div className="flex justify-between items-center ml-6">
            <span className="text-gray-700">
              <span className="mr-2">▪</span>
              Value Added Tax (VAT) at {invoiceData.vatRate}% of Professional Fees
            </span>
            <div className="flex items-center">
              <span className="text-gray-500 mr-4">KES</span>
              <span className="text-gray-800 font-mono w-28 text-right">{formatCurrency(invoiceData.vatAmount)}</span>
            </div>
          </div>
        </div>

        {/* Grand Total */}
        <div className="mx-8 my-6 bg-red-800 text-white px-6 py-4 flex justify-between items-center">
          <span className="font-bold text-lg">Grand Total (amount due)</span>
          <div className="flex items-center">
            <span className="mr-4">KES</span>
            <span className="font-bold text-2xl font-mono">{formatCurrency(invoiceData.grandTotal)}</span>
          </div>
        </div>

        {/* Signature Section */}
        <div className="px-8 py-6 flex justify-between">
          <div>
            <h4 className="text-lg font-bold mb-1">
              <span className="text-red-800">REMINGTON</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-800">LLP</span>
            </h4>
            <p className="text-xs text-gray-600">Megurye Gardens, Office No. 1</p>
            <p className="text-xs text-gray-600">Brookside Gardens, Westlands</p>
            <p className="text-xs text-gray-600">P.O. Box 60030 – 00100</p>
            <p className="text-xs text-gray-600">Nairobi, Kenya</p>
            <p className="text-sm text-gray-700 mt-3">Stamp:</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-800">Remington Otieno</p>
            <p className="text-gray-600">Executive Partner</p>
            <p className="text-red-800">rbo@remingtonllp.com</p>
            <p className="text-sm text-gray-700 mt-3">Sign: _________________</p>
          </div>
        </div>

        {/* Payment Information */}
        <div className="px-8 py-6 border-t-2 border-red-800 bg-gray-50">
          <h4 className="text-red-800 font-bold mb-4">Payment information:</h4>
          <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-sm">
            <div className="flex"><span className="text-gray-600 w-28">Account Name:</span><span className="text-gray-800">Remington Advocates LLP</span></div>
            <div className="flex"><span className="text-gray-600 w-24">Bank Branch:</span><span className="text-gray-800">Buruburu</span></div>
            <div className="flex"><span className="text-gray-600 w-28">Account Number:</span><span className="text-gray-800">0100007335991</span></div>
            <div className="flex"><span className="text-gray-600 w-24">Branch Code:</span><span className="text-gray-800">019</span></div>
            <div className="flex"><span className="text-gray-600 w-28">Bank Name:</span><span className="text-gray-800">Stanbic Bank Kenya Limited</span></div>
            <div className="flex"><span className="text-gray-600 w-24">Swift Code:</span><span className="text-gray-800">SBICKENX</span></div>
            <div className="flex"><span className="text-gray-600 w-28">Bank Code:</span><span className="text-gray-800">31000</span></div>
            <div className="flex"><span className="text-gray-600 w-24">Currency:</span><span className="text-gray-800">Kenyan Shillings (KES)</span></div>
            <div className="flex col-span-2"><span className="text-gray-600 w-28">Address:</span><span className="text-gray-800">Stanbic Bank Centre, Westlands Road, PO Box 30550 – 00100, Nairobi, Kenya</span></div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            See overleaf for the Terms & Conditions of payment. For mobile money payments, credit MPESA Paybill Number 4134025, with the designated Invoice Number as the Account Number for the MPESA Paybill transaction
          </p>
        </div>

        {/* Page Number */}
        <div className="px-8 py-2 text-right text-sm text-gray-600 bg-gray-50 border-t border-gray-200">
          <span className="font-bold">Page</span> 1 of 1
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
